import { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

interface FieldShellProps {
  label: string;
  name: string;
  error?: string;
  hint?: ReactNode;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}

// Label, control, hint and error in one consistent block
export function FieldShell({ label, name, error, hint, optional, className = '', children }: FieldShellProps) {
  return (
    <div className={className}>
      <label htmlFor={name} className="field-label">
        {label}
        {optional && <span className="ml-1 font-normal text-bl-black/45">(optioneel)</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-bl-black/55">{hint}</p>}
      {error && (
        <p id={`${name}-error`} className="field-message" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label: string;
  name: string;
  error?: string;
  hint?: ReactNode;
  optional?: boolean;
  wrapperClassName?: string;
  onValueChange: (value: string) => void;
};

export function TextField({ label, name, error, hint, optional, wrapperClassName, onValueChange, className = '', ...input }: TextFieldProps) {
  return (
    <FieldShell label={label} name={name} error={error} hint={hint} optional={optional} className={wrapperClassName}>
      <input
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`field ${error ? 'field-error' : ''} ${className}`}
        onChange={(e) => onValueChange(e.target.value)}
        {...input}
      />
    </FieldShell>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> & {
  label: string;
  name: string;
  error?: string;
  wrapperClassName?: string;
  onValueChange: (value: string) => void;
};

export function SelectField({ label, name, error, wrapperClassName, onValueChange, children, ...select }: SelectFieldProps) {
  return (
    <FieldShell label={label} name={name} error={error} className={wrapperClassName}>
      <select
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`field ${error ? 'field-error' : ''}`}
        onChange={(e) => onValueChange(e.target.value)}
        {...select}
      >
        {children}
      </select>
    </FieldShell>
  );
}
